// MyResharePools.jsx - what the automatic reposting bot shares on this agent's accounts (core 3830, owner 2026-10-23:
// "allow agents to select filters of what they would like to share on their account by automatic bot ... show them
// counts of each pool ... also select platforms where to share. All are auto selected").
//
// Five pools with their counts (in the pool / matching the filters / already done / left), the portals, property types,
// rent and sale price ranges (USD) and "availability confirmed within N days". An agent sees and sets their own; a
// manager / admin picks any agent from the list the server returns. Nothing ticked by the agent = everything, as before.
// ⚠️ The house rules (pledge, the owner's "do not post", agents' adverts, flats already advertised) are not shown as
// switches: they are not the agent's to turn off. The counts leave the costly pledge / refusal check out - a few flats
// in a pool may still be refused when their turn comes.
import { useEffect, useState } from 'react'
import { t } from '../lib/i18n.js'
import { getResharePrefs, saveResharePrefs } from '../db/core.js'

const POOLS = ['outside', 'coop_ours', 'coop_mine', 'confirmed', 'office']
const PORTALS = [['ss', 'SS.ge'], ['myhome', 'Myhome'], ['korter', 'Korter']]
const KINDS = ['apartment', 'house', 'commercial', 'land']
const INTENTS = ['sale', 'rent']

export function MyResharePools({ embedded = false } = {}) {
  const [open, setOpen] = useState(!embedded)    // inside the worker box it opens on a tap (owner 2026-10-24)
  const [email, setEmail] = useState(null)       // null = me
  const [d, setD] = useState(null)
  const [f, setF] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!open) return
    let gone = false
    setErr(null)
    getResharePrefs(email)
      .then(r => { if (gone) return; setD(r); setF({ ...r.prefs, kinds: r.prefs.kinds || [], intents: r.prefs.intents || INTENTS }); setSaved(false) })
      .catch(e => { if (!gone) { setD(null); setErr(e.message) } })
    return () => { gone = true }
  }, [email, open])

  if (embedded && !open) return (
    <button type="button" onClick={() => setOpen(true)}
      className="mt-2 w-full rounded-md border border-border px-3 py-1.5 text-left text-sm hover:bg-muted">
      {t('desk_pools_title')} ▾
    </button>
  )
  if (!d) return err ? <p className="text-xs text-muted-foreground px-3 py-2">{t('desk_pools_unavailable')}</p> : null
  const count = Object.fromEntries((d.counts || []).map(c => [c.pool, c]))
  const toggle = (key, v) => setF(x => ({ ...x, [key]: x[key].includes(v) ? x[key].filter(y => y !== v) : [...x[key], v] }))
  const numIn = key => (
    <input type="number" min="0" inputMode="numeric" value={f[key] ?? ''}
      onChange={e => setF(x => ({ ...x, [key]: e.target.value === '' ? null : Number(e.target.value) }))}
      className="w-20 min-w-0 rounded-md border border-border bg-background px-2 py-1 text-xs" />
  )
  async function save() {
    setBusy(true); setErr(null)
    try { const r = await saveResharePrefs({ ...f, email: d.email }); setD(r); setF({ ...r.prefs, kinds: r.prefs.kinds || [], intents: r.prefs.intents || INTENTS }); setSaved(true) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }

  return (
    <section className={`${embedded ? 'mt-2 border-t border-border pt-2' : 'p-3'} space-y-3 text-xs`}>
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{t('desk_pools_title')}</h3>
        {embedded && <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground" aria-label="close">▴</button>}
        {d.agents && (
          <select value={d.email} onChange={e => setEmail(e.target.value)}
            className="ml-auto rounded-md border border-border bg-background px-2 py-1 text-xs">
            {d.agents.map(a => <option key={a} value={a}>{a.split('@')[0]}</option>)}
          </select>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-muted-foreground">{t('desk_pools_which')}</p>
        {POOLS.map(p => {
          const c = count[p]
          return (
            <label key={p} className="flex items-center gap-2">
              <input type="checkbox" checked={f.pools.includes(p)} onChange={() => toggle('pools', p)} />
              <span className="flex-1">{t(`desk_pool_${p}`)}</span>
              {c && <span className="tabular-nums text-muted-foreground" title={t('desk_pools_count_hint')}>
                <b className="text-foreground">{c.left_}</b> / {c.matching} / {c.total}
              </span>}
            </label>
          )
        })}
        <p className="text-[11px] text-muted-foreground/80">{t('desk_pools_count_hint')}</p>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span className="w-full text-muted-foreground">{t('desk_pools_portals')}</span>
        {PORTALS.map(([p, label]) => (
          <label key={p} className="flex items-center gap-1.5">
            <input type="checkbox" checked={f.platforms.includes(p)} onChange={() => toggle('platforms', p)} />{label}
          </label>
        ))}
      </div>

      {/* owner 2026-10-24 "can they also select to share only rent or only sale" */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span className="w-full text-muted-foreground">{t('desk_pools_intents')}</span>
        {INTENTS.map(k => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="checkbox" checked={f.intents.includes(k)} onChange={() => toggle('intents', k)} />{t(`desk_pool_intent_${k}`)}
          </label>
        ))}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span className="w-full text-muted-foreground">{t('desk_pools_kinds')}</span>
        {KINDS.map(k => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="checkbox" checked={f.kinds.includes(k)} onChange={() => toggle('kinds', k)} />{t(`desk_pool_kind_${k}`)}
          </label>
        ))}
      </div>

      {/* label above, fields below: the worker box is ~250 px wide and a label + two fields side by side ran off its
          right edge (owner screenshot 2026-10-24) */}
      <div className="space-y-2">
        {[['desk_pools_rent', <>{numIn('rent_min')} – {numIn('rent_max')} $</>],
          ['desk_pools_sale', <>{numIn('sale_min')} – {numIn('sale_max')} $</>],
          ['desk_pools_confirmed', <>{numIn('confirmed_days')} {t('desk_pools_days')}</>]].map(([k, fields]) => (
          <div key={k}>
            <span className="block text-muted-foreground mb-0.5">{t(k)}</span>
            <span className="flex flex-wrap items-center gap-1">{fields}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground/80">{t('desk_pools_confirmed_hint')}</p>

      <div className="flex items-center gap-3">
        <button type="button" disabled={busy || !f.platforms.length || !f.intents.length}
          className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground disabled:opacity-50"
          onClick={save}>{t('desk_pools_save')}</button>
        {saved && <span className="text-emerald-500">{t('desk_pools_saved')}</span>}
        {!f.platforms.length && <span className="text-amber-500">{t('desk_pools_need_portal')}</span>}
        {!f.intents.length && <span className="text-amber-500">{t('desk_pools_need_intent')}</span>}
        {err && <span className="text-red-600">{err}</span>}
      </div>
    </section>
  )
}
